import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Download,
  Eye,
  FileText,
  Search,
  TimerOff,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  downloadClockingJustification,
  previewClockingJustification,
  reviewClockingJustification,
} from "../api/admin-panel";

import type {
  AdminClockingData,
  AdminClockingItem,
} from "../types/admin-panel";
import { ClockingDateRangePicker } from "./ClockingDateRangePicker";
import { JustificationImagePreview } from "./JustificationImagePreview";

type Props = {
  clocking: AdminClockingData | null;
  loading: boolean;
  rankingLoading: boolean;
  selectedRange: { from: string; to: string };
  rankPeriod: "week" | "month" | "year";
  onChangeRankPeriod: (
    period: "week" | "month" | "year",
  ) => Promise<void> | void;
  onChangeRange: (from: string, to: string) => Promise<void> | void;
  onReload: () => Promise<void> | void;
};

const STATUS_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "cerrado", label: "Cerrados" },
  { value: "abierto", label: "Abiertos" },
  { value: "falta", label: "Faltas" },
  { value: "sin_horario", label: "Sin horario" },
];

const ALERT_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "late", label: "Retrasos" },
  { value: "early", label: "Salidas tempranas" },
];

export function ClockingPanel({
  clocking,
  loading,
  rankingLoading,
  selectedRange,
  rankPeriod,
  onChangeRankPeriod,
  onChangeRange,
  onReload,
}: Props) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [alertFilter, setAlertFilter] = useState("all");
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [justificationStatus, setJustificationStatus] = useState("all");
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFilename, setPreviewFilename] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const topAbsenceUsers = useMemo(() => {
    return [...(clocking?.absence_stats ?? [])].sort(
      (a, b) => b.absences - a.absences,
    );
  }, [clocking]);

  const reviewJustification = async (
    id: number,
    estado: "justificada" | "no_justificada",
  ) => {
    setReviewingId(id);

    try {
      await reviewClockingJustification(id, estado);
      await onReload();
    } finally {
      setReviewingId(null);
    }
  };

  const items = clocking?.items ?? [];

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch =
        item.empleado.toLowerCase().includes(search.toLowerCase()) ||
        item.correo.toLowerCase().includes(search.toLowerCase());

      const matchesStatus = status === "all" ? true : item.status === status;

      const matchesAlert =
        alertFilter === "all"
          ? true
          : alertFilter === "late"
            ? item.is_late
            : item.is_early_exit;

      return matchesSearch && matchesStatus && matchesAlert;
    });
  }, [items, search, status, alertFilter]);

  const topLateUsers = useMemo(() => {
    return [...(clocking?.monthly_stats ?? [])].sort(
      (a, b) => b.late_count - a.late_count,
    );
  }, [clocking]);

  const pendingOlder = clocking?.pending_older_justifications ?? {
    count: 0,
    oldest_date: null,
  };

  const filteredJustifications = useMemo(() => {
    const justifications = clocking?.justifications ?? [];
    if (justificationStatus === "all") return justifications;
    return justifications.filter((item) => item.estado_revision === justificationStatus);
  }, [clocking, justificationStatus]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const closePreview = () => {
    setPreviewId(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setPreviewFilename(null);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  };

  const openPreview = async (id: number, filename: string | null) => {
    setPreviewId(id);
    setPreviewFilename(filename);
    setPreviewError(null);
    setPreviewLoading(true);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });

    try {
      const { blob } = await previewClockingJustification(id);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch {
      setPreviewError("No se pudo cargar la vista preliminar de la imagen.");
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-[32px] border border-[#e7ecef] bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,.06)]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              <Clock3 size={14} />
              Control horario
            </div>

            <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950">
              Fichajes y asistencia
            </h2>

            <p className="mt-2 text-sm font-semibold text-slate-500">
              Seguimiento de entradas, salidas, faltas y retrasos.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric
              label="Programados"
              value={clocking?.summary.scheduled_today ?? 0}
              icon={Users}
            />

            <Metric
              label="Fichados"
              value={clocking?.summary.clocked_in ?? 0}
              icon={CheckCircle2}
            />

            <Metric
              label="Faltas"
              value={clocking?.summary.missing ?? 0}
              icon={TimerOff}
            />

            <Metric
              label="Retrasos"
              value={clocking?.summary.late ?? 0}
              icon={AlertTriangle}
            />
          </div>
        </div>
      </section>

      <section className="rounded-[32px] border border-[#e7ecef] bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,.06)]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-lg font-black text-slate-950">
              Estados de fichajes
            </div>

            <div className="mt-1 text-sm font-semibold text-slate-500">
              Control de entradas, salidas y alertas dentro del rango seleccionado.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
              <Search size={16} className="text-slate-400" />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar usuario..."
                className="bg-transparent text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400"
              />
            </div>

            <ClockingDateRangePicker
              from={selectedRange.from}
              to={selectedRange.to}
              disabled={loading}
              onChange={onChangeRange}
            />

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 outline-none"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={alertFilter}
              onChange={(e) => setAlertFilter(e.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 outline-none"
            >
              {ALERT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-[28px] border border-[#edf1f3]">
          <div className="max-h-[540px] overflow-auto">
            <table className="w-full min-w-[1120px] border-separate border-spacing-0">
              <thead className="sticky top-0 z-10">
                <tr>
                  {[
                    "Empleado",
                    "Fecha",
                    "Horario",
                    "Entrada",
                    "Salida",
                    "Estado",
                    "Alertas",
                  ].map((label) => (
                    <th
                      key={label}
                      className="border-b border-[#edf1f3] bg-slate-50 px-5 py-4 text-left text-xs font-black uppercase tracking-[0.14em] text-slate-400"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <SkeletonRows />
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-16 text-center text-sm font-bold text-slate-400"
                    >
                      No hay fichajes para mostrar.
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <ClockingRow key={`${item.usuario_id}-${item.fecha}`} item={item} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="grid gap-6">
        <div className="rounded-[32px] border border-[#e7ecef] bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,.06)]">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700">
                <FileText size={18} />
              </div>

              <div>
                <div className="text-sm font-black text-slate-950">
                  Justificaciones
                </div>

                <div className="text-sm font-semibold text-slate-500">
                  Documentación y comentarios del rango seleccionado.
                </div>
              </div>
            </div>

            <select
              value={justificationStatus}
              onChange={(event) => setJustificationStatus(event.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 outline-none"
            >
              <option value="all">Todas</option>
              <option value="pendiente">Pendientes</option>
              <option value="justificada">Justificadas</option>
              <option value="no_justificada">No justificadas</option>
            </select>
          </div>

          {pendingOlder.count > 0 && (
            <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-black">
                    {pendingOlder.count} justificación(es) pendiente(s) de días anteriores
                  </div>
                  <div className="mt-1 text-xs font-semibold text-amber-700">
                    La más antigua corresponde al {formatDate(pendingOlder.oldest_date)}.
                  </div>
                </div>
              </div>
              {pendingOlder.oldest_date && (
                <button
                  type="button"
                  onClick={() => {
                    const oldest = pendingOlder.oldest_date;
                    if (oldest) void onChangeRange(oldest, oldest);
                  }}
                  className="shrink-0 rounded-xl border border-amber-200 bg-white px-3 py-2 text-xs font-black text-amber-800 transition hover:bg-amber-100"
                >
                  Ver la más antigua
                </button>
              )}
            </div>
          )}

          <div className="max-h-[460px] space-y-3 overflow-y-auto pr-1">
            {loading ? (
              <JustificationSkeletonRows />
            ) : filteredJustifications.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm font-bold text-slate-400">
                No hay justificaciones para los filtros y el rango seleccionados.
              </div>
            ) : (
              filteredJustifications.map((item) => (
                <div
                  key={item.id}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="grid gap-4 lg:grid-cols-[220px_1fr_auto] lg:items-start">
                    <div>
                      <div className="font-black text-slate-950">
                        {item.empleado}
                      </div>

                      <div className="mt-1 text-xs font-black uppercase tracking-[0.14em] text-amber-600">
                        {justificationTypeLabel(item.tipo)}
                      </div>
                    </div>

                    <div>
                      <div className="flex flex-wrap gap-2 text-xs font-bold text-slate-500">
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1">
                          Fecha: {formatDate(item.fecha)}
                        </span>
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1">
                          Tipo:{" "}
                          {item.clock_action === "entrada"
                            ? "Entrada"
                            : "Salida"}
                        </span>

                        {item.hora_fichaje && (
                          <span className="rounded-full border border-slate-200 bg-white px-3 py-1">
                            Hora: {item.hora_fichaje}
                          </span>
                        )}

                        {item.horario && (
                          <span className="rounded-full border border-slate-200 bg-white px-3 py-1">
                            Horario: {item.horario.hora_inicio} -{" "}
                            {item.horario.hora_fin}
                          </span>
                        )}

                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1">
                          Estado: {reviewStatusLabel(item.estado_revision)}
                        </span>
                      </div>

                      {item.comentario && (
                        <p className="mt-3 text-sm font-semibold leading-6 text-slate-500">
                          {item.comentario}
                        </p>
                      )}

                      {item.documento_nombre && (
                        <div className="mt-3 text-xs font-bold text-slate-400">
                          {item.documento_nombre}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap justify-start gap-2 lg:justify-end">
                      {item.documento_path && isImageJustification(item.documento_mime, item.documento_nombre) && (
                        <button
                          onClick={() => void openPreview(item.id, item.documento_nombre)}
                          className="inline-flex items-center gap-2 rounded-xl border border-violet-100 bg-violet-50 px-3 py-2 text-xs font-black text-violet-700 transition-all hover:bg-violet-100"
                        >
                          <Eye size={14} />
                          Vista preliminar
                        </button>
                      )}

                      {item.documento_path && (
                        <button
                          onClick={() => downloadClockingJustification(item.id)}
                          className="inline-flex items-center gap-2 rounded-xl border border-cyan-100 bg-cyan-50 px-3 py-2 text-xs font-black text-cyan-700 transition-all hover:bg-emerald-100"
                        >
                          <Download size={14} />
                          Descargar
                        </button>
                      )}

                      {item.estado_revision === "pendiente" ? (
                        <>
                          <button
                            onClick={() =>
                              reviewJustification(item.id, "justificada")
                            }
                            disabled={reviewingId === item.id}
                            className="inline-flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700 transition-all hover:bg-emerald-100 disabled:opacity-60"
                          >
                            Justificar
                          </button>

                          <button
                            onClick={() =>
                              reviewJustification(item.id, "no_justificada")
                            }
                            disabled={reviewingId === item.id}
                            className="inline-flex items-center gap-2 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2 text-xs font-black text-rose-700 transition-all hover:bg-rose-100 disabled:opacity-60"
                          >
                            No justificada
                          </button>
                        </>
                      ) : (
                        <span
                          className={[
                            "inline-flex rounded-xl border px-3 py-2 text-xs font-black",
                            item.estado_revision === "justificada"
                              ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                              : "border-rose-100 bg-rose-50 text-rose-700",
                          ].join(" ")}
                        >
                          {reviewStatusLabel(item.estado_revision)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-[32px] border border-[#e7ecef] bg-white p-5 shadow-[0_20px_50px_rgba(15,23,42,.06)]">
          <div className="mb-4 text-sm font-black text-slate-950">
            Periodo de rankings
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { value: "week", label: "Semanal" },
              { value: "month", label: "Mensual" },
              { value: "year", label: "Anual" },
            ].map((item) => (
              <button
                key={item.value}
                onClick={() =>
                  onChangeRankPeriod(item.value as "week" | "month" | "year")
                }
                className={[
                  "rounded-2xl px-5 py-4 text-sm font-black transition-all",
                  rankPeriod === item.value
                    ? "bg-emerald-500 text-white shadow-[0_16px_34px_rgba(16,185,129,.22)]"
                    : "border border-slate-200 bg-slate-50 text-slate-600 hover:bg-white",
                ].join(" ")}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <div className="rounded-[32px] border border-[#e7ecef] bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,.06)]">
            <div className="mb-5 flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl border border-rose-100 bg-rose-50 text-rose-700">
                <AlertTriangle size={18} />
              </div>

              <div>
                <div className="text-sm font-black text-slate-950">
                  Ranking retrasos
                </div>

                <div className="text-sm font-semibold text-slate-500">
                  Empleados con más retrasos según el periodo seleccionado.
                </div>
              </div>
            </div>

            <div className="max-h-[430px] space-y-3 overflow-y-auto pr-1">
              {rankingLoading ? (
                <RankingSkeletonRows />
              ) : (
                topLateUsers.map((user, index) => (
                  <div
                    key={user.usuario_id}
                    className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                  >
                    <div>
                      <div className="font-black text-slate-950">
                        #{index + 1} {user.empleado}
                      </div>

                      <div className="mt-1 text-xs font-semibold text-slate-500">
                        {user.correo}
                      </div>
                    </div>

                    <div className="rounded-full border border-rose-100 bg-rose-50 px-3 py-1 text-xs font-black text-rose-700">
                      {user.late_count} retrasos
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-[32px] border border-[#e7ecef] bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,.06)]">
            <div className="mb-5 flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl border border-amber-100 bg-amber-50 text-amber-700">
                <TimerOff size={18} />
              </div>

              <div>
                <div className="text-sm font-black text-slate-950">
                  Ranking faltas
                </div>

                <div className="text-sm font-semibold text-slate-500">
                  Empleados con más faltas según el periodo seleccionado.
                </div>
              </div>
            </div>

            <div className="max-h-[430px] space-y-3 overflow-y-auto pr-1">
              {rankingLoading ? (
                <RankingSkeletonRows />
              ) : (
                topAbsenceUsers.map((user, index) => (
                  <div
                    key={user.usuario_id}
                    className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                  >
                    <div>
                      <div className="font-black text-slate-950">
                        #{index + 1} {user.empleado}
                      </div>

                      <div className="mt-1 text-xs font-semibold text-slate-500">
                        {user.correo}
                      </div>
                    </div>

                    <div className="rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-xs font-black text-amber-700">
                      {user.absences} faltas
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      <JustificationImagePreview
        open={previewId !== null}
        imageUrl={previewUrl}
        filename={previewFilename}
        loading={previewLoading}
        error={previewError}
        onClose={closePreview}
        onDownload={() => {
          if (previewId !== null) void downloadClockingJustification(previewId);
        }}
      />
    </div>
  );
}

function ClockingRow({ item }: { item: AdminClockingItem }) {
  return (
    <tr>
      <td className="border-b border-[#edf1f3] px-5 py-4">
        <div className="font-black text-slate-950">{item.empleado}</div>

        <div className="mt-1 text-sm font-semibold text-slate-500">
          {item.correo}
        </div>
      </td>

      <td className="whitespace-nowrap border-b border-[#edf1f3] px-5 py-4 text-sm font-black text-slate-600">
        {formatDate(item.fecha)}
      </td>

      <td className="border-b border-[#edf1f3] px-5 py-4">
        {item.schedule ? (
          <div className="flex flex-col gap-1">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-600">
              <CalendarClock size={14} />
              {item.schedule.hora_inicio} - {item.schedule.hora_fin}
            </div>
            {item.is_vacation_day && item.vacation_jornada !== "completa" && (
              <span className="text-xs font-bold text-emerald-600">
                Horario ajustado por vacaciones
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {item.is_rest_day ? <Badge tone="sky">Descanso plantilla</Badge> : <Badge tone="slate">Sin horario</Badge>}
            {item.is_rest_day && (
              <span className="text-xs font-bold text-sky-600">
                {item.rest_day_name ?? "Día de descanso de empresa"}
              </span>
            )}
            {item.is_vacation_day && (
              <span className="text-xs font-bold text-slate-400">
                Vacaciones aprobadas
              </span>
            )}
          </div>
        )}
      </td>

      <td className="border-b border-[#edf1f3] px-5 py-4 font-bold text-slate-700">
        {item.hora_entrada ?? "—"}
      </td>

      <td className="border-b border-[#edf1f3] px-5 py-4 font-bold text-slate-700">
        {item.hora_salida ?? "—"}
      </td>

      <td className="border-b border-[#edf1f3] px-5 py-4">
        <StatusBadge status={item.status} />
      </td>

      <td className="border-b border-[#edf1f3] px-5 py-4">
        <div className="flex flex-wrap gap-2">
          {item.is_late && <Badge tone="amber">Retraso</Badge>}
          {item.is_early_exit && <Badge tone="rose">Salida temprana</Badge>}
          {item.is_justified && <Badge tone="emerald">Justificada</Badge>}

          {!item.is_late && !item.is_early_exit && !item.is_justified && (
            <span className="text-sm font-semibold text-slate-400">
              Sin alertas
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function Metric({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: typeof Clock3;
}) {
  return (
    <div className="rounded-2xl border border-emerald-100 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">
            {label}
          </div>

          <div className="mt-1 text-2xl font-black text-slate-950">{value}</div>
        </div>

        <div className="grid h-10 w-10 place-items-center rounded-2xl bg-emerald-50 text-emerald-700">
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: AdminClockingItem["status"] }) {
  const map = {
    sin_horario: {
      label: "Sin horario",
      tone: "slate",
    },
    falta: {
      label: "Falta",
      tone: "rose",
    },
    abierto: {
      label: "Abierto",
      tone: "sky",
    },
    cerrado: {
      label: "Cerrado",
      tone: "emerald",
    },
  } as const;

  return <Badge tone={map[status].tone}>{map[status].label}</Badge>;
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "emerald" | "sky" | "rose" | "amber" | "slate";
}) {
  const classes = {
    emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
    sky: "border-sky-100 bg-sky-50 text-sky-700",
    rose: "border-rose-100 bg-rose-50 text-rose-700",
    amber: "border-amber-100 bg-amber-50 text-amber-700",
    slate: "border-slate-200 bg-slate-50 text-slate-500",
  };

  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-black ${classes[tone]}`}
    >
      {children}
    </span>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, row) => (
        <tr key={row}>
          {Array.from({ length: 7 }).map((__, col) => (
            <td key={col} className="border-b border-[#edf1f3] px-5 py-4">
              <div className="h-5 w-28 animate-pulse rounded-xl bg-slate-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

function reviewStatusLabel(
  value: import("../types/admin-panel").AdminClockingJustification["estado_revision"],
) {
  const labels = {
    pendiente: "Pendiente",
    justificada: "Justificada",
    no_justificada: "No justificada",
  };

  return labels[value];
}

function justificationTypeLabel(type: string) {
  const labels: Record<string, string> = {
    entrada_tarde_horario: "Entrada tarde",
    entrada_antes_horario: "Entrada antes de horario",
    salida_antes_horario: "Salida antes de horario",
    salida_despues_horario: "Salida después de horario",
    olvido_fichaje: "Olvido de fichaje",
    otros: "Otros",
  };

  return labels[type] ?? type;
}
function JustificationSkeletonRows() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
        >
          <div className="h-5 w-40 animate-pulse rounded-xl bg-slate-100" />
          <div className="mt-3 h-4 w-full max-w-[520px] animate-pulse rounded-xl bg-slate-100" />
          <div className="mt-3 h-4 w-64 animate-pulse rounded-xl bg-slate-100" />
        </div>
      ))}
    </>
  );
}
function RankingSkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
        >
          <div className="h-5 w-40 animate-pulse rounded-xl bg-slate-100" />
          <div className="mt-2 h-4 w-28 animate-pulse rounded-xl bg-slate-100" />
        </div>
      ))}
    </>
  );
}

function isImageJustification(mime: string | null, filename: string | null): boolean {
  if (mime?.toLowerCase().startsWith("image/")) return true;
  if (!filename) return false;
  return /\.(png|jpe?g|gif|webp|bmp)$/i.test(filename);
}
